import CloseIcon from "@mui/icons-material/CloseRounded";
import { Avatar, Button, DialogContent, DialogTitle, IconButton, Stack, Typography } from "@mui/material";
import { useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import SmallDialog from "@entrilia/shared/components/SmallDialog";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import DashedArrowLeftIcon from "@entrilia/shared/icons/DashedArrowLeftIcon";
import DashedArrowRightIcon from "@entrilia/shared/icons/DashedArrowRightIcon";
import adminApi from "../../../../api/adminApi";
import DataBackupIcon from "../../../common/domain-icons/DataBackupIcon";
import aws from "../../../../images/aws.png";

const backupNow = withErrorHandling(adminApi.runDataBackupNow);

interface Props {
  onCancel: () => void;
  onBackupStarted: () => void;
  onOperationInProgress: () => void;
  onConnectionFailed: (msg: string) => void;
}

const BackupNowDialog = ({ onCancel, onBackupStarted, onOperationInProgress, onConnectionFailed }: Props) => {
  const { sendNotificationError } = useNotificationContext();
  const [loading, setLoading] = useState(false);

  const handleRunBackup = async () => {
    setLoading(true);
    const [backupResponse, error] = await backupNow();
    if (error) {
      sendNotificationError("An error occurred while starting the backup.");
    } else if (backupResponse.previousSyncInProgress) {
      onOperationInProgress();
    } else if (backupResponse.connectionFailed) {
      onConnectionFailed(
        "An error occurred while establishing a connection with AWS. You can view all logs in the Logs tab."
      );
    } else if (backupResponse.success) {
      onBackupStarted();
    } else {
      sendNotificationError("An error occurred while starting the backup.");
    }
    setLoading(false);
  };

  return (
    <SmallDialog open={true} onClose={onCancel}>
      <DialogTitle>
        <Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h6" noWrap>
            Backup now
          </Typography>
          <IconButton onClick={onCancel}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent sx={(t) => ({ px: t.spacing(3), pb: t.spacing(2.5) })}>
        <Stack sx={(t) => ({ pt: t.spacing(2.5) })} spacing={4}>
          <Stack spacing={2.5} direction="row" sx={{ justifyContent: "center", alignItems: "center" }}>
            <DataBackupIcon sx={{ width: 64, height: 64 }} />
            <Stack spacing={1}>
              <DashedArrowRightIcon color="primary" sx={{ width: 61, height: 10 }} />
              <DashedArrowLeftIcon sx={(theme) => ({ width: 61, height: 10, color: theme.palette.divider })} />
            </Stack>
            <Avatar src={aws} variant="rounded" alt="logo" sx={{ width: 64, height: 64 }} />
          </Stack>
          <Stack spacing={2} sx={{ textAlign: "center" }}>
            <Typography variant="subtitle2">Backup to AWS</Typography>
            <Typography>
              You are about to start a full data backup operation. This backup is expected to take several minutes, and
              its progress can be tracked in the Logs tab.
            </Typography>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogButtons onCancel={onCancel}>
        <Button variant="contained" color="primary" loading={loading} onClick={handleRunBackup}>
          Start
        </Button>
      </DialogButtons>
    </SmallDialog>
  );
};

export default BackupNowDialog;
